import React from 'react';

export interface SyntaxHighlighterProps {
  code: string;
  language: 'bash' | 'curl' | 'javascript' | 'js' | 'html';
  className?: string;
}

interface Token {
  text: string;
  type:
    | 'plain'
    | 'comment'
    | 'string'
    | 'keyword'
    | 'tag'
    | 'attr-name'
    | 'attr-value'
    | 'flag'
    | 'number'
    | 'boolean'
    | 'builtin'
    | 'punctuation';
}

function tokenizeHtml(code: string): Token[] {
  const tokens: Token[] = [];
  const regex =
    /(<!--[\s\S]*?-->)|(<\/?[a-zA-Z0-9-]+)|(\/?>)|([a-zA-Z_:][-a-zA-Z0-9_:.]*(?=\s*=))|(=)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(code)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ text: code.slice(lastIndex, match.index), type: 'plain' });
    }

    if (match[1]) {
      tokens.push({ text: match[1], type: 'comment' });
    } else if (match[2]) {
      tokens.push({ text: match[2], type: 'tag' });
    } else if (match[3]) {
      tokens.push({ text: match[3], type: 'tag' });
    } else if (match[4]) {
      tokens.push({ text: match[4], type: 'attr-name' });
    } else if (match[5]) {
      tokens.push({ text: match[5], type: 'punctuation' });
    } else if (match[6]) {
      tokens.push({ text: match[6], type: 'attr-value' });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < code.length) {
    tokens.push({ text: code.slice(lastIndex), type: 'plain' });
  }

  return tokens;
}

function tokenizeBash(code: string): Token[] {
  const tokens: Token[] = [];
  const regex =
    /(#[^\n]*)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|(-[a-zA-Z0-9-]+|--[a-zA-Z0-9-]+)|(\b(?:curl|POST|GET|PUT|DELETE|PATCH|Bearer)\b)|(\\)|([{}[\],:])/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(code)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ text: code.slice(lastIndex, match.index), type: 'plain' });
    }

    if (match[1]) {
      tokens.push({ text: match[1], type: 'comment' });
    } else if (match[2]) {
      tokens.push({ text: match[2], type: 'string' });
    } else if (match[3]) {
      tokens.push({ text: match[3], type: 'flag' });
    } else if (match[4]) {
      tokens.push({ text: match[4], type: 'keyword' });
    } else if (match[5]) {
      tokens.push({ text: match[5], type: 'punctuation' });
    } else if (match[6]) {
      tokens.push({ text: match[6], type: 'punctuation' });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < code.length) {
    tokens.push({ text: code.slice(lastIndex), type: 'plain' });
  }

  return tokens;
}

function tokenizeJs(code: string): Token[] {
  const tokens: Token[] = [];
  const regex =
    /(\/\/[^\n]*)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`)|(\b(?:const|let|var|await|async|function|return|new)\b)|(\b(?:fetch|JSON\.stringify|JSON\.parse|console\.log|response\.json)\b)|(\b(?:true|false|null|undefined|\d+(?:\.\d+)?)\b)|(\b(?:method|headers|body)\b(?=\s*:))|([{}()[\].,;:])/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(code)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ text: code.slice(lastIndex, match.index), type: 'plain' });
    }

    if (match[1]) {
      tokens.push({ text: match[1], type: 'comment' });
    } else if (match[2]) {
      tokens.push({ text: match[2], type: 'string' });
    } else if (match[3]) {
      tokens.push({ text: match[3], type: 'keyword' });
    } else if (match[4]) {
      tokens.push({ text: match[4], type: 'builtin' });
    } else if (match[5]) {
      tokens.push({ text: match[5], type: 'number' });
    } else if (match[6]) {
      tokens.push({ text: match[6], type: 'attr-name' });
    } else if (match[7]) {
      tokens.push({ text: match[7], type: 'punctuation' });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < code.length) {
    tokens.push({ text: code.slice(lastIndex), type: 'plain' });
  }

  return tokens;
}

function getTokenClass(type: Token['type']): string {
  switch (type) {
    case 'comment':
      return 'text-zinc-500 italic';
    case 'string':
    case 'attr-value':
      return 'text-emerald-300';
    case 'tag':
      return 'text-cf-orange font-semibold';
    case 'attr-name':
      return 'text-sky-300';
    case 'keyword':
      return 'text-purple-400 font-medium';
    case 'builtin':
      return 'text-blue-400 font-medium';
    case 'flag':
      return 'text-cyan-400 font-semibold';
    case 'number':
    case 'boolean':
      return 'text-amber-400';
    case 'punctuation':
      return 'text-zinc-500';
    case 'plain':
    default:
      return 'text-zinc-200';
  }
}

export const SyntaxHighlighter: React.FC<SyntaxHighlighterProps> = ({
  code,
  language,
  className = '',
}) => {
  let tokens: Token[];
  if (language === 'html') {
    tokens = tokenizeHtml(code);
  } else if (language === 'javascript' || language === 'js') {
    tokens = tokenizeJs(code);
  } else {
    tokens = tokenizeBash(code);
  }

  return (
    <pre
      className={`p-4 bg-[#0d0d0d] font-mono text-xs overflow-auto select-text leading-relaxed ${className}`}
    >
      <code>
        {tokens.map((token, index) => (
          <span key={index} className={getTokenClass(token.type)}>
            {token.text}
          </span>
        ))}
      </code>
    </pre>
  );
};
